/** Dependency-free Supabase Auth and same-origin Worker adapter. */
export interface CloudConfig {
  configured: boolean;
  supabaseUrl: string;
  publishableKey: string;
  publicBaseUrl: string;
  schema: string;
  emailSendingEnabled: boolean;
}

export interface CloudSession {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  user: { id: string; email?: string };
}

export interface CloudWorkspace<T = Record<string, unknown>> {
  workspace: T | null;
  revision: number;
  updatedAt: string | null;
}

export class CloudError extends Error {
  status: number;
  code: string;
  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'CloudError';
    this.status = status;
    this.code = code;
  }
}

const SESSION_KEY = 'apollo-studio:cloud-session:v1';
let memorySession: CloudSession | null = null;
let refreshInFlight: Promise<CloudSession> | null = null;

function storeSession(session: CloudSession | null) {
  memorySession = session;
  try {
    if (session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(SESSION_KEY);
  } catch { /* Memory-only sessions still work when browser storage is unavailable. */ }
}

/** Tokens use sessionStorage and expire with the tab; no passwords are stored. */
export function getSession(): CloudSession | null {
  if (memorySession) return memorySession;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const candidate = JSON.parse(raw);
    if (!candidate || typeof candidate.access_token !== 'string' || typeof candidate.refresh_token !== 'string' || typeof candidate.expires_at !== 'number' || typeof candidate.user?.id !== 'string') return null;
    memorySession = candidate;
    return memorySession;
  } catch { return null; }
}

async function checkedJson<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok) {
    const detail = body.error && typeof body.error === 'object' ? body.error as Record<string, unknown> : body;
    const code = String(detail.code || body.error_code || 'CLOUD_ERROR');
    const authMessages: Record<string, string> = {
      email_not_confirmed: 'Your email address has not been confirmed. Open your confirmation email or request another below, then sign in again.',
      invalid_credentials: 'The email address or password is incorrect. Check both and try again.',
      over_email_send_rate_limit: 'Too many confirmation emails have been requested. Wait a few minutes before trying again.',
      over_request_rate_limit: 'Too many requests. Wait a few minutes before trying again.',
      email_address_not_authorized: 'The authentication email provider cannot send to this address. Ask the workspace owner to check Supabase Auth email delivery settings.',
    };
    throw new CloudError(
      authMessages[code] || (response.status === 429 ? 'Too many requests. Wait a few minutes before trying again.' : String(detail.message || body.error_description || body.msg || body.message || 'Cloud request failed.')),
      response.status,
      code,
    );
  }
  return body as T;
}

export async function cloudConfig(): Promise<CloudConfig> {
  return checkedJson<CloudConfig>(await fetch('/api/config', { cache: 'no-store' }));
}

async function configured() {
  const cfg = await cloudConfig();
  if (!cfg.configured) throw new CloudError('Cloud storage is not configured. Your local workspace is available.', 503, 'CLOUD_NOT_CONFIGURED');
  return cfg;
}

function parseSession(result: { access_token: string; refresh_token: string; expires_at?: number; expires_in?: number; user: { id: string; email?: string; is_anonymous?: boolean } }): CloudSession {
  if (!result.access_token || !result.refresh_token || !result.user?.id || result.user.is_anonymous === true) throw new CloudError('A permanent account is required.', 401, 'INVALID_SESSION');
  return { access_token: result.access_token, refresh_token: result.refresh_token, expires_at: result.expires_at || Math.floor(Date.now() / 1000) + (result.expires_in || 3600), user: { id: result.user.id, email: result.user.email } };
}

export async function login(email: string, password: string): Promise<CloudSession> {
  const cfg = await configured();
  const result = await checkedJson<Parameters<typeof parseSession>[0]>(await fetch(`${cfg.supabaseUrl}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: { apikey: cfg.publishableKey, 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  }));
  const session = parseSession(result);
  storeSession(session);
  return session;
}

/** Resends only an existing signup confirmation; never creates an account or logs in. */
export async function resendConfirmationEmail(email: string): Promise<void> {
  const address = email.trim();
  if (address.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) throw new CloudError('Enter your account email address before requesting a confirmation email.', 400, 'INVALID_EMAIL');
  const cfg = await configured();
  let redirect: URL;
  try {
    const base = new URL(cfg.publicBaseUrl);
    const production = base.protocol === 'https:' && /^[a-z0-9-]+\.rigoonstudio\.com$/.test(base.hostname) && !base.port;
    const local = base.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(base.hostname);
    if ((!production && !local) || base.username || base.password || base.search || base.hash || base.pathname !== '/') throw new Error();
    redirect = new URL('/auth/confirm', base.origin);
  } catch { throw new CloudError('The confirmation return address is not configured correctly. Ask the workspace owner to check the public app URL.', 503, 'AUTH_REDIRECT_NOT_CONFIGURED'); }
  const endpoint = new URL('/auth/v1/resend', cfg.supabaseUrl);
  endpoint.searchParams.set('redirect_to', redirect.href);
  await checkedJson(await fetch(endpoint.href, {
    method: 'POST',
    headers: { apikey: cfg.publishableKey, 'content-type': 'application/json' },
    body: JSON.stringify({ email: address, type: 'signup' }),
  }));
}

async function refreshCurrentSession(): Promise<CloudSession> {
  const current = getSession();
  if (!current) throw new CloudError('Sign in before using cloud storage.', 401, 'UNAUTHENTICATED');
  const cfg = await configured();
  try {
    const result = await checkedJson<Parameters<typeof parseSession>[0]>(await fetch(`${cfg.supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST', headers: { apikey: cfg.publishableKey, 'content-type': 'application/json' },
      body: JSON.stringify({ refresh_token: current.refresh_token }),
    }));
    const session = parseSession(result);
    storeSession(session);
    return session;
  } catch (error) {
    if (error instanceof CloudError && [400, 401, 403].includes(error.status)) storeSession(null);
    throw error;
  }
}

/** A rotated refresh token must be used once, even when several views load together. */
export async function refreshSession(): Promise<CloudSession> {
  if (!refreshInFlight) {
    refreshInFlight = refreshCurrentSession().finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

async function authenticatedSession() {
  const session = getSession();
  if (!session) throw new CloudError('Sign in before using cloud storage.', 401, 'UNAUTHENTICATED');
  return session.expires_at <= Math.floor(Date.now() / 1000) + 30 ? refreshSession() : session;
}

export async function logout(): Promise<void> {
  const current = getSession();
  try {
    if (current) {
      const cfg = await configured();
      await checkedJson(await fetch(`${cfg.supabaseUrl}/auth/v1/logout?scope=local`, { method: 'POST', headers: { apikey: cfg.publishableKey, authorization: `Bearer ${current.access_token}` } }));
    }
  } finally { storeSession(null); }
}

export async function loadCloudWorkspace<T = Record<string, unknown>>(): Promise<CloudWorkspace<T>> {
  const session = await authenticatedSession();
  return checkedJson<CloudWorkspace<T>>(await fetch('/api/workspace', { cache: 'no-store', headers: { authorization: `Bearer ${session.access_token}` } }));
}

export async function saveCloudWorkspace<T extends object>(workspace: T, revision: number): Promise<CloudWorkspace<T>> {
  const session = await authenticatedSession();
  return checkedJson<CloudWorkspace<T>>(await fetch('/api/workspace', {
    method: 'PUT',
    headers: { authorization: `Bearer ${session.access_token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ workspace, revision }),
  }));
}
